import { Button, Select } from '@/src/components/Antd';
import SelectObjectAsValue from '@/src/components/SelectObjectAsValue';
import InputMaxLength from '@/src/pages/Settings/Components/InputMaxLength';
import {
  getPickupLocationGeo,
  getPickupLocationPage,
} from '@/src/request/settings/logistics/pickupLocation';
import {
  getWareHouseLocationGeo,
  getWareHouseLocationPage,
} from '@/src/request/settings/logistics/wareHouseLocation';
import { getState, getCity } from '@/src/request/settings/storeDetails';
import { unitTypeList } from '@/src/tools/config';
import { ConditionType } from '@/src/types/common/common';
import { CaretDownOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Col, Form, Input, Row, Spin } from 'antd';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import styles from '../style.module.less';
import {
  PhoneNumber,
  AreaCodeInfo,
} from 'src/pages/CustomerDatabaseConfig/Components/CustomerInfoModal';

const RenderFormItem = (props) => {
  const { value, labelKey } = props;
  return (
    <div className={styles.renderFormItem}>
      {value && !!labelKey ? value[labelKey] : value}
    </div>
  );
};

const ProvinceSelect = ({ country, province, form, onEditable }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const { loading: getProvinceLoading, run: _getProvinces } = useRequest(
    (p) => getState(p),
    {
      manual: true,
      onSuccess(result) {
        setProvinceOptions(result);
        if (!result.length) {
          form.setFieldsValue({
            province: null,
          });
        }
      },
    },
  );

  const { loading: getCitiesLoading, run: _getCities } = useRequest(
    (p) => getCity(p),
    {
      manual: true,
      onSuccess(result) {
        setCityOptions(result);
        if (!result.length) {
          form.setFieldsValue({
            city: null,
          });
        }
      },
    },
  );

  useEffect(() => {
    if (country && country.code) {
      _getProvinces(country.code);
    }
  }, [country]);

  useEffect(() => {
    if (province && province.uuid) {
      _getCities(province.uuid);
    }
  }, [province]);

  return (
    <Spin spinning={getProvinceLoading || getCitiesLoading}>
      <Row>
        <Col span={11}>
          <Form.Item
            label={
              <div className={styles.label}>
                {getI18n('storeLogistics.packingSlip.stateProvinceDistrict')}*
              </div>
            }
            name={'province'}
            rules={[
              {
                required: true,
              },
            ]}>
            {onEditable ? (
              <SelectObjectAsValue
                objectAsValue
                options={provinceOptions}
                labelKey={'name'}
                valueKey={'uuid'}
                size='large'
                notFoundContent={
                  <div>{getI18n('storeLogistics.components.noresult')}</div>
                }
                suffixIcon={
                  <CaretDownOutlined className={styles.selectIcon} />
                }></SelectObjectAsValue>
            ) : (
              <RenderFormItem labelKey={'name'} />
            )}
          </Form.Item>
        </Col>
        <Col span={12} offset={1}>
          <Form.Item
            label={
              <div className={styles.label}>
                {getI18n('storeLogistics.packingSlip.city')}*
              </div>
            }
            name={'city'}
            rules={[
              {
                required: true,
              },
            ]}>
            {onEditable ? (
              <SelectObjectAsValue
                objectAsValue
                options={cityOptions}
                labelKey={'name'}
                valueKey={'uuid'}
                size='large'
                notFoundContent={
                  <div>{getI18n('storeLogistics.components.noresult')}</div>
                }
                suffixIcon={
                  <CaretDownOutlined className={styles.selectIcon} />
                }></SelectObjectAsValue>
            ) : (
              <RenderFormItem labelKey={'name'} />
            )}
          </Form.Item>
        </Col>
      </Row>
    </Spin>
  );
};

const Common = ({ form, from, onEditable }: any) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { loading, data } = useRequest(
    from == 'pickupLocation' ? getPickupLocationPage : getWareHouseLocationPage,
  );

  const {
    loading: getWareHouseLocationGeoLoading,
    run: _getWareHouseLocationGeo,
  } = useRequest(
    (params) =>
      from == 'pickupLocation'
        ? getPickupLocationGeo(params)
        : getWareHouseLocationGeo(params),
    {
      manual: true,
      onSuccess(result) {
        if (result && result.latitude && result.longitude) {
          form.setFieldsValue({
            latitude: result.latitude,
            longitude: result.longitude,
          });
        }
      },
    },
  );

  const { countries = [] } = data || {};

  // const areaCodeOptions = countries.map((c) => ({
  //   label: '+' + c.areaCode,
  //   value: c.areaCode,
  // }));

  const onGetReferenceClick = () => {
    const { addressLine, country, province } = form.getFieldsValue([
      'addressLine',
      'country',
      'province',
    ]);

    // if (addressLine && country && province) {
    _getWareHouseLocationGeo({
      addressLine,
      country,
      province,
    });
    // }
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.commonFormItem}>
        <div className={styles.primaryContent}>
          <Row>
            <Col span={12}>
              <Form.Item
                label={
                  <div className={styles.labelRow}>
                    <div className={styles.label}>
                      {getI18n('storeLogistics.components.locationName')}*
                    </div>
                    {onEditable && (
                      <div className={styles.labelRight}>
                        <InputMaxLength
                          maxLength={100}
                          fieldName='locationName'
                        />
                      </div>
                    )}
                  </div>
                }
                name={'locationName'}
                rules={[
                  {
                    required: true,
                  },
                ]}>
                {onEditable ? (
                  <Input size='large' maxLength={100} />
                ) : (
                  <RenderFormItem />
                )}
              </Form.Item>
            </Col>
            <Col span={11} offset={1}>
              <Row>
                <Form.Item
                  label={<div className={styles.label}>Phone*</div>}
                  name={'areaCodeInfo'}
                  //initialValue='+852'
                  rules={[
                    {
                      required: true,
                    },
                  ]}>
                  <AreaCodeInfo displayOnly={!onEditable} />
                  {/* {onEditable ? (
                      <SelectObjectAsValue
                        options={areaCodeOptions}
                        size='large'
                        notFoundContent={
                          <div>
                            {getI18n('storeLogistics.components.noresult')}
                          </div>
                        }
                        suffixIcon={
                          <CaretDownOutlined className={styles.selectIcon} />
                        }></SelectObjectAsValue>
                    ) : (
                      <RenderFormItem type='areaCode' />
                    )} */}
                </Form.Item>

                <Form.Item
                  style={{ flex: 1 }}
                  label={<div className={styles.label}></div>}
                  name={'phone'}
                  rules={[
                    {
                      required: true,
                    },
                    // {
                    //   pattern: /^\+?[1-9]\d*$/,
                    //   message: 'phone should be number',
                    // },
                  ]}>
                  {onEditable ? <PhoneNumber /> : <RenderFormItem />}
                </Form.Item>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                label={
                  <div className={styles.label}>
                    {getI18n('storeLogistics.components.addressLine')}*
                  </div>
                }
                name={'addressLine'}
                rules={[
                  {
                    required: true,
                  },
                ]}>
                {onEditable ? <Input.TextArea rows={4} /> : <RenderFormItem />}
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.twoInOneRow}>
            <Col span={12}>
              <Form.Item
                label={
                  <div className={styles.label}>
                    {getI18n('storeLogistics.packingSlip.countryRegion')}*
                  </div>
                }
                name={'country'}
                rules={[
                  {
                    required: true,
                  },
                ]}>
                {onEditable ? (
                  <SelectObjectAsValue
                    objectAsValue
                    showSearch
                    labelKey={'description'}
                    valueKey={'code'}
                    options={countries}
                    size='large'
                    notFoundContent={
                      <div>{getI18n('storeLogistics.components.noresult')}</div>
                    }
                    filterOption={(input, option) =>
                      (get(option, 'label', '') as unknown as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    suffixIcon={
                      <CaretDownOutlined className={styles.selectIcon} />
                    }></SelectObjectAsValue>
                ) : (
                  <RenderFormItem labelKey={'description'} />
                )}
              </Form.Item>
            </Col>
            <Col span={11} offset={1}>
              <Form.Item
                style={{ flex: 1 }}
                shouldUpdate={(p, c) =>
                  p.country !== c.country || p.province !== c.province
                }>
                {({ getFieldValue }) => {
                  const country = getFieldValue('country');
                  const province = getFieldValue('province');

                  return (
                    <ProvinceSelect
                      country={country}
                      province={province}
                      form={form}
                      onEditable={onEditable}
                    />
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Item
                label={
                  <div className={styles.label}>
                    {getI18n('storeLogistics.packingSlip.postcodeZip')}
                  </div>
                }
                name={'postcode'}>
                {onEditable ? (
                  <Input size='large' maxLength={200} />
                ) : (
                  <RenderFormItem />
                )}
              </Form.Item>
            </Col>
          </Row>
        </div>
        <div className={styles.latlong}>
          {onEditable && (
            <div className={styles.headerText}>
              <div className={styles.title}>
                {getI18n('storeLogistics.components.latitudeandLongitude')}
              </div>
              <div className={styles.description}>
                {getI18n('storeLogistics.components.commonDesc1')}
                <br />
                {getI18n('storeLogistics.components.commonDesc2')}
              </div>
            </div>
          )}

          <Spin spinning={getWareHouseLocationGeoLoading}>
            <Row
              justify='space-between'
              className={`${styles.twoInOneRow} ${
                !onEditable && styles.editTwoInOneRow
              }`}>
              <Col>
                <Form.Item
                  label={
                    <div className={styles.label}>
                      {getI18n('storeLogistics.components.latitude')}
                    </div>
                  }
                  name={'latitude'}>
                  {onEditable ? (
                    <Input size='large' maxLength={200} />
                  ) : (
                    <RenderFormItem />
                  )}
                </Form.Item>
              </Col>
              <Col>
                <Form.Item
                  label={
                    <div className={styles.label}>
                      {getI18n('storeLogistics.components.longitude')}
                    </div>
                  }
                  name={'longitude'}>
                  {onEditable ? (
                    <Input size='large' maxLength={200} />
                  ) : (
                    <RenderFormItem />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Spin>
          {onEditable && (
            <div className={styles.referenceData}>
              <Button className={styles.button} onClick={onGetReferenceClick}>
                {getI18n('storeLogistics.components.getReferenceData')}
              </Button>
            </div>
          )}
          <Row>
            <Col span={12}>
              <Form.Item
                className={styles.unit}
                label={
                  <div className={styles.label}>
                    {getI18n('storeLogistics.components.defaultUnitofWeight')}
                  </div>
                }
                rules={[
                  {
                    required: true,
                    message: getI18n(
                      'storeLogistics.components.defaultUnitofWeightIsRequired',
                    ),
                  },
                ]}
                name={'defaultWeightUnit'}>
                {onEditable ? (
                  <Select
                    size='large'
                    optionsGroupList={unitTypeList[ConditionType.Weight]}
                  />
                ) : (
                  <RenderFormItem />
                )}
              </Form.Item>
            </Col>
          </Row>
        </div>
      </div>
    </Spin>
  );
};
export default Common;
